import { Fragment } from 'react';

import { Text } from '@spotify-internal/encore-web';
import { extractLastNameComponent } from '@spotify/confidence-core-react';

import type { PreviewField } from '../../extensions';
import { useResolveResourceName } from '../ToolConfirmationMessage/ResourceDisplayNameContext';
import {
  getNestedValue,
  hasNestedKey,
  humanizeKey,
} from '../ToolConfirmationMessage/inputUtils';
import {
  SUPPLEMENTAL_RESOURCE_TYPES,
  getResourceType,
  isResourceName,
} from '../ToolConfirmationMessage/resourceNames';

import styles from './ConfirmationConfigGrid.module.css';

const RESOURCE_PATH_PATTERN = /^\/?[^/\s"'=\\]+(?:\/[^/\s"'=\\]+)+$/;

const resolveStringValue = (
  value: string,
  resolve: (name: string) => string,
): string => {
  const resolved = resolve(value);
  if (resolved !== value) return resolved;
  if (
    isResourceName(value) &&
    SUPPLEMENTAL_RESOURCE_TYPES.some(
      supplementalType => supplementalType === getResourceType(value),
    )
  ) {
    return value;
  }
  return RESOURCE_PATH_PATTERN.test(value)
    ? (extractLastNameComponent(value) ?? value)
    : value;
};

const ConfigValue = ({ value }: { value: unknown }) => {
  const resolve = useResolveResourceName();

  if (value === null || value === undefined) return null;

  if (typeof value === 'string') {
    return <Text variant="marginal">{resolveStringValue(value, resolve)}</Text>;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return <Text variant="marginal">{String(value)}</Text>;
  }

  if (Array.isArray(value)) {
    const display = value
      .map(v => {
        if (typeof v === 'string') return resolveStringValue(v, resolve);
        if (typeof v === 'object' && v !== null) {
          const obj = v as Record<string, unknown>;
          const label = obj.displayName ?? obj.name ?? obj.variant;
          if (label !== undefined) {
            const raw = String(label);
            return resolveStringValue(raw, resolve);
          }
          return JSON.stringify(v);
        }
        return String(v);
      })
      .join(', ');
    return <Text variant="marginal">{display}</Text>;
  }

  return <Text variant="marginal">{JSON.stringify(value)}</Text>;
};

interface ConfirmationConfigGridProps {
  input: Record<string, unknown>;
  previewFields: PreviewField[];
}

export const ConfirmationConfigGrid = ({
  input,
  previewFields,
}: ConfirmationConfigGridProps) => {
  const visibleFields = previewFields.filter(f => hasNestedKey(input, f.key));

  if (visibleFields.length === 0) return null;

  return (
    <div className={styles.configGrid}>
      {visibleFields.map(field => (
        <Fragment key={field.key}>
          <Text variant="marginal" semanticColor="textSubdued">
            {field.label ??
              humanizeKey(field.key.split('.').pop() ?? field.key)}
          </Text>
          <ConfigValue value={getNestedValue(input, field.key)} />
        </Fragment>
      ))}
    </div>
  );
};
